import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';

import { UserAccountService } from '../../../services/user-account/user-account.service';
import {
  BillingDetails,
  IndianState,
} from '../../../models/billing-details.model';

@Component({
  selector: 'app-billing-details-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './billing-details-dialog.component.html',
  styleUrls: ['./billing-details-dialog.component.css'],
})
export class BillingDetailsDialogComponent implements OnInit {
  @Input() existingDetails: BillingDetails | null = null;
  @Input() title = 'Billing Details';
  @Input() subtitle =
    'These details will be printed on your invoices. You can update them later.';
  @Output() saved = new EventEmitter<BillingDetails>();
  @Output() closed = new EventEmitter<void>();

  isSaving = false;
  states: IndianState[] = [];
  form: BillingDetails = this.emptyForm();
  errors: Record<string, string> = {};

  constructor(
    private userAccountService: UserAccountService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.loadBillingDetails();
  }

  get isBusiness(): boolean {
    return this.form.billing_type === 'business';
  }

  onBackdropClick(): void {
    if (!this.isSaving) {
      this.closed.emit();
    }
  }

  onBillingTypeChange(type: 'individual' | 'business'): void {
    this.form.billing_type = type;
    if (type === 'individual') {
      this.errors['company_name'] = '';
      this.errors['gstin'] = '';
    }
  }

  onSubmit(): void {
    if (!this.validate()) {
      this.toastr.error('Please fix the highlighted billing fields.');
      return;
    }

    this.isSaving = true;
    const payload = {
      ...this.form,
      company_name: this.form.company_name?.trim() || null,
      address_line2: this.form.address_line2?.trim() || null,
      gstin: this.form.gstin?.trim().toUpperCase() || null,
      country: this.form.country || 'India',
    };

    this.userAccountService.saveBillingDetails(payload).subscribe({
      next: (response) => {
        const savedDetails = response?.data?.billing_details || payload;
        this.toastr.success(
          response?.message_key || 'Billing details saved successfully',
        );
        this.isSaving = false;
        this.saved.emit(savedDetails);
      },
      error: (error) => {
        this.isSaving = false;
        this.toastr.error(
          error?.error?.message_key || 'Failed to save billing details.',
        );
      },
    });
  }

  private loadBillingDetails(): void {
    this.userAccountService.getBillingDetails().subscribe({
      next: (response) => {
        this.states = response?.data?.states?.length
          ? response.data.states
          : [];
        const details =
          response?.data?.billing_details || this.existingDetails || null;
        if (details) {
          this.form = {
            ...this.emptyForm(),
            ...details,
            gstin: details.gstin || '',
            company_name: details.company_name || '',
            address_line2: details.address_line2 || '',
          };
        }
      },
      error: () => {
        this.toastr.error('Unable to load billing details.');
      },
    });
  }

  private emptyForm(): BillingDetails {
    return {
      billing_type: 'individual',
      name: '',
      company_name: '',
      email: '',
      phone: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
      gstin: '',
    };
  }

  private validate(): boolean {
    this.errors = {};
    const required: Array<keyof BillingDetails> = [
      'name',
      'email',
      'phone',
      'address_line1',
      'city',
      'state',
      'pincode',
    ];

    required.forEach((field) => {
      if (!String(this.form[field] || '').trim()) {
        this.errors[field] = 'This field is required';
      }
    });

    if (this.form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) {
      this.errors['email'] = 'Enter a valid email';
    }

    if (
      this.form.phone &&
      !/^(\+91[\s-]?)?[6-9]\d{9}$/.test(this.form.phone.trim())
    ) {
      this.errors['phone'] = 'Enter a valid 10-digit mobile number';
    }

    if (this.form.pincode && !/^[1-9][0-9]{5}$/.test(this.form.pincode.trim())) {
      this.errors['pincode'] = 'Enter a valid 6-digit PIN code';
    }

    if (this.isBusiness) {
      if (!String(this.form.company_name || '').trim()) {
        this.errors['company_name'] = 'Company name is required';
      }
      if (
        !this.form.gstin ||
        !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
          this.form.gstin.trim().toUpperCase(),
        )
      ) {
        this.errors['gstin'] = 'Enter a valid 15-character GSTIN';
      }
    } else if (
      this.form.gstin &&
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
        this.form.gstin.trim().toUpperCase(),
      )
    ) {
      this.errors['gstin'] = 'Enter a valid 15-character GSTIN';
    }

    return Object.keys(this.errors).length === 0;
  }
}
